// Which loaded clouds are one site, mirroring the engine's rule (siteBox in
// geo-engine.js): clouds whose own sites lie within SITE_JOIN_M, or twice the
// larger one's radius, of each other join; the site holding most points is
// the main one. The base map's pin and its grid check use the main site, so a
// file in another frame (an LGSX left in its scan project frame, a file never
// registered) no longer drags them halfway to it.

export interface Vec3 { x: number; y: number; z: number }
export interface CloudSite { min: Vec3; max: Vec3; points: number }

export const SITE_JOIN_M = 1000;

const half = (b: { min: Vec3; max: Vec3 }) => Math.hypot(b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z) / 2;
const gap = (a: CloudSite, b: CloudSite) => Math.hypot(
  Math.max(a.min.x - b.max.x, 0, b.min.x - a.max.x),
  Math.max(a.min.y - b.max.y, 0, b.min.y - a.max.y),
  Math.max(a.min.z - b.max.z, 0, b.min.z - a.max.z),
);

export function mainSite(clouds: CloudSite[]): { min: Vec3; max: Vec3; points: number } | null {
  if (!clouds.length) return null;
  const parent = clouds.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
    return i;
  };
  for (let i = 0; i < clouds.length; i++) {
    for (let j = i + 1; j < clouds.length; j++) {
      if (gap(clouds[i], clouds[j]) <= Math.max(SITE_JOIN_M, 2 * Math.max(half(clouds[i]), half(clouds[j])))) parent[find(i)] = find(j);
    }
  }
  const sites = new Map<number, { min: Vec3; max: Vec3; points: number }>();
  clouds.forEach((c, i) => {
    const r = find(i);
    const s = sites.get(r);
    if (!s) { sites.set(r, { min: { ...c.min }, max: { ...c.max }, points: c.points }); return; }
    s.min = { x: Math.min(s.min.x, c.min.x), y: Math.min(s.min.y, c.min.y), z: Math.min(s.min.z, c.min.z) };
    s.max = { x: Math.max(s.max.x, c.max.x), y: Math.max(s.max.y, c.max.y), z: Math.max(s.max.z, c.max.z) };
    s.points += c.points;
  });
  return [...sites.values()].sort((a, b) => b.points - a.points)[0];
}
